import React, { useEffect, useState } from "react";
import { Bounce, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useAuth } from "../../../../context/AuthContext";
import classes from "./AddContinualImprovement.module.css";
import CommonFooter from "../../../CommonFooter/CommonFooter";
import { env } from "../../../../constants/env";
import QuestionSection from "../../../ui/QuestionSection/QuestionSection";

const questionSets = [
  {
    render: (onAnswerChange, answers, editable) => (
      <>
        <QuestionSection
          number="11.1"
          title="Procedure and/or Process for Continual Improvement"
          questions={[
            {
              content: (
                <>
                  Has the laboratory defined a procedure and/or process that
                  addresses, but is not limited to, the following?
                  <br />
                  Note: Improvement activities must be identified within the
                  pre-examination, examination, and post-examination processes.
                  Laboratory management shall ensure that the laboratory
                  participates in continual improvement activities that
                  encompass relevant areas and outcomes of patient care and
                  results records
                </>
              ),
              hideOptions: true,
            },
            {
              content:
                "Identification of improvement activities within the laboratory management system;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Development and documentation of improvement plans;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Communication of improvement plans and related goals to relevant personnel;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Implementation of action plans;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Recording of improvement plans;",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Evaluation of effectiveness of actions taken.",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="11.2"
          title="Implementation of Continual Improvement of Management System"
          questions={[
            {
              content: (
                <>
                  Does the laboratory identify and undertake continual quality
                  improvement activities?
                  <br />
                  Note: The laboratory should use its management review
                  activities to continually improve its laboratory management
                  system by comparing its actual performance to its intentions
                  stated in the quality policy and objectives
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="11.3"
          title="Communication of Continual Improvement Activities"
          questions={[
            {
              content: (
                <>
                  Are the outcomes of continual improvement activities
                  communicated to laboratory management, personnel, and users?
                  <br />
                  Note 1: The communication can be done using graphical tools
                  (such as charts, graphs, tables) and in personnel and
                  management meetings. <br />
                  Note 2: Examples of graphical tools commonly used for this
                  purpose include LJ charts, Pareto charts, cause-and-effect
                  diagrams, frequency histograms, trend graphs, and flow charts.
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
      </>
    ),
  },
];

const AddContinualImprovement = () => {
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const { user, token } = useAuth();
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const { labId, questionnaireId } = useParams(); // ✅ get questionnaireId too

  const editable = questionnaireId ? status === "Not Attached" : true;

  // --- Prefill answers if editing ---
  useEffect(() => {
    const fetchQuestionnaire = async () => {
      if (!questionnaireId) return; // new form
      try {
        setLoading(true);
        const response = await fetch(
          `${env.kataApiUrl}/api/certification/id/${questionnaireId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to load questionnaire");
        }

        const data = await response.json();

        console.log("DATATAA", data);

        setStatus(data.status);

        const prefilled = {};
        if (data.answers && Array.isArray(data.answers)) {
          data.answers.forEach((ans) => {
            prefilled[ans.questionNumber] = {
              score: ans.score,
              comment: ans.comment,
              points: ans.points,
            };
          });
        }

        setAnswers(prefilled);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [questionnaireId, token]);

  const handleAnswerChange = (questionNumber, score, comment, points) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionNumber]: {
        score,
        comment,
        points: points ? Number(points) : undefined,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const submitAssessment = async () => {
    const answersArray = Object.entries(answers).map(
      ([questionNumber, data]) => ({
        questionNumber,
        score: data.score,
        comment: data.comment,
        points: data.points,
      })
    );

    const payload = {
      name: "SLIPTA",
      labId,
      section: "Continual Improvement",
      auditorId: user.id,
      answers: answersArray,
    };

    const url = questionnaireId
      ? `${env.kataApiUrl}/api/certification/id/${questionnaireId}` // edit
      : `${env.kataApiUrl}/api/certification/submit`; // create

    const method = questionnaireId ? "PUT" : "POST";

    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Submission failed");
    }

    return await response.json();
  };

  const handleDone = async () => {
    try {
      if (Object.keys(answers).length === 0) {
        toast.warning("Please answer at least one question before submitting");
        return;
      }

      await submitAssessment();

      toast.success(
        questionnaireId ? "updated successfully!" : "Submitted successfully!"
      );

      navigate(-1);
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    }
  };

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div
        className="d-flex align-items-center gap-2 mb-4"
        onClick={() => navigate(-1)}
        role="button"
      >
        <IoIosArrowRoundBack size={35} />
        <h2 className="m-0">Section 11: Continual Improvement {questionnaireId && "(Editing)"}</h2>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {questionSets[currentSet].render(
            handleAnswerChange,
            answers,
            editable
          )}

          <CommonFooter
            handlePrevious={handlePrevious}
            handleCancel={() => navigate(-1)}
            handleNext={handleNext}
            handleDone={handleDone}
            questionSets={questionSets}
            currentSet={currentSet}
            cancelText="Cancel"
            doneText={
              questionnaireId ? "Update Assessment" : "Submit Assessment"
            }
          />
        </>
      )}
    </div>
  );
};

export default AddContinualImprovement;
